import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import api from "../api";
import "../styles/loader.css";

const MonthConsumption = () => {
  const userType = localStorage.getItem("user_type");
  const companyId = localStorage.getItem("company_id");

  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");

  const [type, setType] = useState("");

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(new Date().getMonth() + 1);

  const [data, setData] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [loading, setLoading] = useState(false);

  // ===============================
  // FETCH CLIENTS
  // ===============================
  useEffect(() => {
    const fetchClients = async () => {
      try {
        let is_shared_param = null;

        if (type === "0") is_shared_param = 0;
        else if (type === "1") is_shared_param = 1;

        const res = await api.get("/companies", {
          params:
            is_shared_param !== null
              ? { is_shared: is_shared_param }
              : {},
        });

        const sorted = res.data.sort((a, b) =>
          (a.company_name || "").localeCompare(
            b.company_name || "",
            "en",
            { sensitivity: "base" }
          )
        );

        setClients([
          { company_id: "ALL", company_name: "ALL" },
          ...sorted,
        ]);
      } catch (err) {
        console.error("Client fetch error:", err);
      }
    };

    if (userType === "Super-Admin" || userType === "Admin") {
      fetchClients();
    } else {
      setSelectedClient(companyId);
    }
  }, [type]);

  // ===============================
  // API CALL
  // ===============================
  const fetchReport = async () => {
    const payload = { year, month, type };

    if (selectedClient && selectedClient !== "ALL") {
      payload.company_id = selectedClient;
    }

    const res = await api.post(
      "/report/company_consumption_month",
      payload
    );

    return res.data;
  };

  // ===============================
  // VIEW
  // ===============================
  const handleView = async () => {
      setLoading(true);
      try {
        const res = await fetchReport();

        if (!res?.data?.length) {
          alert("No data found");
          setShowTable(false);
          return;
        }

        const rows = res.data.map((row) => ({
          client: row.Company_Name || "-",
          type: row.Company_Type || "-",

          pulseDay: Number(row.IB_Talk_Minutes || 0),
          pulseNight: Number(row.IBN_Talk_Minutes || 0),
          pulseAb: Number(row.OB_Talk_Minutes || 0),

          dayRate: row.day_Rate || "-",
          nightRate: row.night_Rate || "-",
          abRate: row.ab_Rate || "-",

          total: Number(row.Total_Consume || 0).toFixed(2),
        }));

        setData(rows);
        setShowTable(true);
      } catch (err) {
        console.error(err);
        alert("Error fetching data");
      } finally {
        setLoading(false);
      }
    };

  // ===============================
  // EXPORT
  // ===============================
  const handleExport = async () => {
  setLoading(true);
  try {
    const res = await fetchReport();

    if (!res?.data?.length) {
      alert("No data available");
      return;
    }

    const exportRows = res.data.map((row) => ({
      "Client": row.Company_Name || "-",
      "Type": row.Company_Type || "-",

      "Pulse Day": Number(row.IB_Talk_Minutes || 0),
      "Pulse Night": Number(row.IBN_Talk_Minutes || 0),
      "Pulse AB": Number(row.OB_Talk_Minutes || 0),

      "Day Rate": row.day_Rate || "-",
      "Night Rate": row.night_Rate || "-",
      "AB Rate": row.ab_Rate || "-",

      "Total (Rs.)": Number(row.Total_Consume || 0).toFixed(2),
    }));

    const monthName = [
      "Jan","Feb","Mar","Apr","May","Jun",
      "Jul","Aug","Sep","Oct","Nov","Dec"
    ][month - 1];

    const selectedClientObj = clients.find(
      (c) => String(c.company_id) === String(selectedClient)
    );

    const clientLabel = selectedClientObj
      ? selectedClientObj.company_name
      : "All Clients";

    // ✅ ONE LINE HEADER
    const headerRows = [
      [`Month Consumption Report | Client: ${clientLabel} | Month: ${monthName} ${year}`],
      [],
    ];

    const worksheet = XLSX.utils.json_to_sheet(exportRows, {
      origin: "A3",
    });

    XLSX.utils.sheet_add_aoa(worksheet, headerRows, { origin: "A1" });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Month Consumption"
    );

    const buffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    saveAs(
      new Blob([buffer]),
      `MonthConsumption_${monthName}_${year}.xlsx`
    );

  } catch (err) {
    console.error(err);
    alert("Export failed");
  } finally {
    setLoading(false);
  }
};

  return (
    <>
      {/* LOADER */}
      {loading && (
        <div className="loader-overlay">
          <div className="bar"></div>
          <div className="bar"></div>
          <div className="bar"></div>
          <div className="bar"></div>
          <div className="bar"></div>
        </div>
      )}

      <div className={`priority-wrapper ${loading ? "blurred" : ""}`}>
        <div className="card p-4 mb-4">
          <h5>Month Consumption</h5>

          <div className="d-flex gap-3 flex-wrap align-items-center">

            {/* TYPE */}
            <select
              className="form-control w-auto"
              value={type}
              onChange={(e) => {
                setType(e.target.value);
                setSelectedClient("");
              }}
            >
              <option value="">All</option>
              <option value="0">Dedicated</option>
              <option value="1">Shared</option>
            </select>

            {/* CLIENT */}
            {(userType === "Admin" ||
              userType === "Super-Admin") && (
              <select
                className="form-control w-20"
                value={selectedClient}
                onChange={(e) =>
                  setSelectedClient(e.target.value)
                }
              >
                {clients.map((c) => (
                  <option key={c.company_id} value={c.company_id}>
                    {c.company_name}
                  </option>
                ))}
              </select>
            )}

            {/* YEAR */}
            <select
              className="form-control w-auto"
              value={year}
              onChange={(e) => setYear(e.target.value)}
            >
              {Array.from(
                { length: 10 },
                (_, i) => currentYear - 5 + i
              ).map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>

            {/* MONTH */}
            <select
              className="form-control w-auto"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            >
              {[
                "Jan","Feb","Mar","Apr","May","Jun",
                "Jul","Aug","Sep","Oct","Nov","Dec"
              ].map((m, i) => (
                <option key={i + 1} value={i + 1}>
                  {m}
                </option>
              ))}
            </select>

            <button className="btn btn-primary" onClick={handleView}>
              View
            </button>

            <button className="btn btn-success" onClick={handleExport}>
              Export
            </button>
          </div>
        </div>

        {/* TABLE */}
        {showTable && (
          <div className="card p-4">
            <div
              className="table-responsive"
              style={{ maxHeight: "600px", overflowY: "auto" }}
            >
              <table className="table table-bordered">
                <thead
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "#fff",
                    zIndex: 1,
                  }}
                >
                  <tr>
                    <th>Client</th>
                    <th>Type</th>
                    <th>Pulse Day</th>
                    <th>Pulse Night</th>
                    <th>Pulse AB</th>
                    <th>Day Rate</th>
                    <th>Night Rate</th>
                    <th>AB Rate</th>
                    <th>Total (Rs.)</th>
                  </tr>
                </thead>

                <tbody>
                  {data.map((r, i) => (
                    <tr key={i}>
                      <td>{r.client}</td>
                      <td>{r.type}</td>
                      <td>{r.pulseDay}</td>
                      <td>{r.pulseNight}</td>
                      <td>{r.pulseAb}</td>
                      <td>{r.dayRate}</td>
                      <td>{r.nightRate}</td>
                      <td>{r.abRate}</td>
                      <td>{r.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MonthConsumption;